import React from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import getDocumentList from './get-product-documents';
import { PropertyType } from '../propertyTypeEnum';

import addProductWithOrderAttributes, { IAllAddressesWithDocs } from './add-product-with-order-attributes';
import checkDuplicateAtCart from './duplicate-address-at-cart';
import {
    getATSSectionNumberDetailAsync,
    getCommunityAsync,
    getElectronicBlockProfileDocIdsAsync
} from '../../../../customProxy/retailExtension/DataActionExtension.g';
import {
    ICommunity,
    IElectronicBlockProfile,
    IGetATSSectionNumberDataResponse
} from '../../../../customProxy/retailExtension/DataServiceEntities.g';
import Spinner from '../../views/custom-components/spinner';
import TypeAheadInput, { IOption } from '../../views/custom-components/type-ahead-input';
import { IDocumentsCategroyForm, IFormInput, IResponseResult, IVerifiedProducts } from '../types/buybox';
import { RichTextComponent } from '@msdyn365-commerce/core-internal';
import { format } from '@msdyn365-commerce/retail-proxy';

const GenericForm: React.FC<IDocumentsCategroyForm> = props => {
    const { isDCModalOpen, closeDCModal, restProps, propertyType, setModalOpen } = props;
    const {
        data: {
            product: { result: product }
        }
    } = restProps;
    const { loadingLabel, addToCartButtonLabel, noItemsSelectedLabel, selectQuadrantsValNE } = restProps.resources;
    const { selectQuadrantsValNW, selectQuadrantsValSE, selectQuadrantsValSW, selectQuadrantsLabel } = restProps.resources;
    const { communityNameLabel, quadrantLabel, atsLabel, blockProfileLabel, isErrorSuffix } = restProps.resources;
    const { removeLabel, internalServerError, invalidSearchAddressQueryErrorMessage } = restProps.resources;
    const { serviceUnavialableErrorText, defaultFormHeader, closeModalLabel, requiredErrorSuffix } = restProps.resources;
    const { ebpSearchInstructions, atsSearchInstructions } = restProps.resources;
    const searchInstructions =
        propertyType === PropertyType.ats ? atsSearchInstructions : propertyType === PropertyType.blockProfile ? ebpSearchInstructions : '';
    const initialData: IFormInput = {
        value: '',
        shortName: '',
        errorMessage: '',
        label:
            propertyType === PropertyType.community
                ? communityNameLabel
                : propertyType === PropertyType.quadrant
                ? quadrantLabel
                : propertyType === PropertyType.ats
                ? atsLabel
                : blockProfileLabel,
        documentId: '',
        isOptionSelected: false
    };
    const [dcFields, setDCFields] = React.useState<IFormInput>(initialData);
    const [docsList, setDocsList] = React.useState<IResponseResult[]>([]);
    const [verifiedProducts, setVerifiedProducts] = React.useState<IVerifiedProducts[]>([]);
    const [showLoader, setShowLoader] = React.useState(false);
    const [documentListErrorMessage, setDocumentListErrorMessage] = React.useState('');
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const staticSearchBtnRef = React.useRef<HTMLButtonElement>(null);
    const searchBtnRef = React.useRef<HTMLButtonElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useEffect(() => {
        dcFields.isOptionSelected && searchBtnRef.current && searchBtnRef.current.focus();
    }, [dcFields.isOptionSelected]);
    let isServiceUnavailable = false;
    const handleCommunityChange = (value: string) => {
        const updatedFields = { ...dcFields };
        if (value.length > 0) {
            updatedFields.value = value;
            updatedFields.isOptionSelected = false;
            updatedFields.errorMessage = '';
        } else {
            updatedFields.value = value;
            updatedFields.isOptionSelected = false;
            updatedFields.errorMessage = `${updatedFields.label}${isErrorSuffix}${requiredErrorSuffix}.`;
        }
        if (docsList.length > 0) {
            setDocsList([]);
        }
        if (documentListErrorMessage) {
            setDocumentListErrorMessage('');
        }
        setErrorMessages([]);
        setDCFields(updatedFields);
    };
    const handleQuadrantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        if (value != '') {
            const updatedFields = { ...dcFields };
            if (value.length > 0) {
                updatedFields.value = value;
                updatedFields.isOptionSelected = true;
                updatedFields.errorMessage = '';
            } else {
                updatedFields.value = value;
                updatedFields.isOptionSelected = false;
                updatedFields.errorMessage = `${updatedFields.label} is required.`;
            }
            if (docsList.length > 0) {
                setDocsList([]);
            }
            if (documentListErrorMessage) {
                setDocumentListErrorMessage('');
            }
            setDCFields(updatedFields);
        }
        setErrorMessages([]);
    };
    const onAddNewItem = async () => {
        setShowLoader(true);
        const checkedDocs = docsList.filter(doc => doc.isChecked === true);
        const localProduct = {
            searchKey: dcFields.value,
            isChecked: true
        };
        const duplicates: { isState: boolean; doc: IResponseResult }[] = [];
        const productsToAdd: IResponseResult[] = [];
        const existingDocList = verifiedProducts.find(product => product.searchKey === localProduct.searchKey)?.documentList;

        for (const doc of checkedDocs) {
            const duplicateCart = await checkDuplicateAtCart({}, restProps.context, product?.ItemId, {
                documentId: doc.documentId!,
                propertyType: propertyType,
                searchKey: localProduct.searchKey,
                documentName: doc.documentName
            });
            if (duplicateCart) {
                duplicates.push({ isState: false, doc });
            } else if (existingDocList) {
                const duplicateDoc = existingDocList.find(oldDoc => doc.documentId === oldDoc.documentId);
                if (duplicateDoc) {
                    duplicates.push({ isState: true, doc });
                } else {
                    existingDocList.push(doc);
                }
            } else {
                productsToAdd.push(doc);
            }
        }
        const { duplicateInCartErrorMessage, duplicateInStateErrorMessage } = restProps.resources;
        setErrorMessages(
            duplicates.map(({ isState, doc }) => {
                const message = isState ? duplicateInStateErrorMessage : duplicateInCartErrorMessage;
                return format(message, doc.documentName);
            })
        );
        existingDocList
            ? setVerifiedProducts([...verifiedProducts])
            : setVerifiedProducts([...verifiedProducts, { ...localProduct, documentList: productsToAdd }]);
        clearValues();
        setShowLoader(false);
    };
    const clearValues = () => {
        setDocsList([]);
        setDCFields(initialData);
    };

    const getOptionGetter = () => {
        const getCommunityNames = async (value: string) => {
            let response: ICommunity[] | null;
            try {
                response = await getCommunityAsync({ callerContext: restProps.context.actionContext }, value.trim());
            } catch (error) {
                //@ts-ignore
                if (error?.status?.toLowerCase() === serviceUnavialableErrorText.toLowerCase()) {
                    isServiceUnavailable = true;
                }
                response = null;
            }
            const searchOptions: IOption[] = [];
            if (response?.length) {
                for (const community of response) {
                    community && searchOptions.push({ name: community.Name!, shortName: community.ShortName! });
                }
            } else {
                setDCFields(prevState => ({
                    ...prevState,
                    errorMessage: isServiceUnavailable
                        ? internalServerError
                        : `${invalidSearchAddressQueryErrorMessage}: ${prevState.value}`,
                    isAddressSelected: false
                }));
            }
            return searchOptions;
        };
        const getEBPNumbers = async (value: string) => {
            let response: IElectronicBlockProfile | null;
            try {
                response = await getElectronicBlockProfileDocIdsAsync({ callerContext: restProps.context.actionContext }, value.trim());
            } catch (error) {
                //@ts-ignore
                if (error?.status?.toLowerCase() === serviceUnavialableErrorText.toLowerCase()) {
                    isServiceUnavailable = true;
                }
                response = null;
            }
            const searchOptions: IOption[] = [];
            if (response?.DocIdList && response.DocIdList.length >= 1) {
                for (const ids of response.DocIdList) {
                    ids && searchOptions.push({ name: ids, shortName: ids });
                }
            } else {
                setDCFields(prevState => ({
                    ...prevState,
                    errorMessage: isServiceUnavailable
                        ? internalServerError
                        : `${invalidSearchAddressQueryErrorMessage}: ${prevState.value}`,
                    isAddressSelected: false
                }));
            }
            return searchOptions;
        };
        const getATSNumbers = async (value: string) => {
            let response: IGetATSSectionNumberDataResponse | null;
            try {
                response = await getATSSectionNumberDetailAsync({ callerContext: restProps.context.actionContext }, value.trim());
            } catch (error) {
                //@ts-ignore
                if (error?.status?.toLowerCase() === serviceUnavialableErrorText.toLowerCase()) {
                    isServiceUnavailable = true;
                }
                response = null;
            }
            const searchOptions: IOption[] = [];
            if (response?.ATSSectionNumber && response.ATSSectionNumber.length >= 1) {
                for (const ats of response.ATSSectionNumber) {
                    ats && searchOptions.push({ name: ats, shortName: ats });
                }
            } else {
                setDCFields(prevState => ({
                    ...prevState,
                    errorMessage: isServiceUnavailable
                        ? internalServerError
                        : `${invalidSearchAddressQueryErrorMessage}: ${prevState.value}`,
                    isAddressSelected: false
                }));
            }
            return searchOptions;
        };
        if (propertyType === PropertyType.community) {
            return getCommunityNames;
        } else if (propertyType === PropertyType.blockProfile) {
            return getEBPNumbers;
        } else if (propertyType === PropertyType.ats) {
            return getATSNumbers;
        }
        return async () => [];
    };

    const handleCommunityNameSelection = (communityName: IOption, shouldSwitchControl?: boolean) => {
        setDCFields(prevState => ({
            ...prevState,
            value: communityName.name || '',
            shortName: communityName.shortName || '',
            isOptionSelected: true
        }));
        if (shouldSwitchControl) {
            searchBtnRef.current = staticSearchBtnRef.current;
        } else {
            searchBtnRef.current = null;
        }
    };

    const mapProductsToAddresses = async () => {
        const productsToAdd = verifiedProducts.filter(product => product.isChecked);

        const allAddresses = productsToAdd.map(verifiedProduct => {
            const customAddress: IAllAddressesWithDocs = {
                addressVerified: {},
                legalDescriptionCondoVerified: {},
                legalDescriptionVerified: {},
                lookForVerified: {},
                propertyType: propertyType,
                serviceAddress: {},
                sdab: {},
                lcsab: {},
                relatedProduct: [product!],
                communityVerified: {},
                quadrantVerified: {},
                blockProfileVerified: {},
                atsVerified: {},
                documentList: verifiedProduct.documentList
            };
            switch (propertyType) {
                case PropertyType.community:
                    if (customAddress.communityVerified) {
                        customAddress.communityVerified.communityName = verifiedProduct.searchKey;
                    }
                    break;
                case PropertyType.quadrant:
                    if (customAddress.quadrantVerified) {
                        customAddress.quadrantVerified.quadrant = verifiedProduct.searchKey;
                    }
                    break;
                case PropertyType.blockProfile:
                    if (customAddress.blockProfileVerified) {
                        customAddress.blockProfileVerified.blockProfile = verifiedProduct.searchKey;
                    }
                    break;
                case PropertyType.ats:
                    if (customAddress.atsVerified) {
                        customAddress.atsVerified.atsValue = verifiedProduct.searchKey;
                    }
                    break;
                default:
                    break;
            }
            return customAddress;
        });
        return allAddresses;
    };
    /* VSI-Customization -- START Render address options for desired address search query  */

    const handleDocumentsCategroyFormSubmission = async () => {
        setShowLoader(true);
        const allAddresses = await mapProductsToAddresses();
        await addProductWithOrderAttributes(allAddresses, restProps.context);
        setShowLoader(false);
        setModalOpen(false);
        clearValues();
        setErrorMessages([]);
        setVerifiedProducts([]);
    };
    const onSearchClick = async () => {
        setShowLoader(true);
        if (docsList.length > 0) {
            setDocsList([]);
        }
        if (documentListErrorMessage) {
            setDocumentListErrorMessage('');
        }
        const { productTypePA } = restProps.context.app.config;
        const prodType: string =
            restProps.data.productAttributes.result
                ?.find(x => x.Name === productTypePA)
                ?.TextValue?.toLowerCase()
                .trim() || '';

        const value =
            propertyType === PropertyType.community
                ? prodType === PropertyType[PropertyType.document]
                    ? dcFields.value
                    : dcFields.shortName
                : dcFields.value;
        if (value && dcFields.isOptionSelected) {
            const docList = await getDocumentList(restProps, product!, value!, propertyType, setDocumentListErrorMessage, clearValues);
            if (docList?.length) {
                setDocsList([...docList]);
            } else {
                inputRef.current?.focus();
            }
        } else {
            setDCFields(prevState => ({
                ...prevState,
                errorMessage: `${prevState.label} is required.`
            }));
        }
        setShowLoader(false);
    };
    const handleDocumentCheck = (selectedDoc: IResponseResult) => {
        const index = docsList.findIndex(doc => doc.documentId === selectedDoc.documentId);
        if (index > -1) {
            docsList[index].isChecked = !docsList[index].isChecked;
        }
        setDocsList([...docsList]);
    };
    const onProductClickChange = (index: number, docIndex: number) => {
        verifiedProducts[index].documentList[docIndex].isChecked = !verifiedProducts[index].documentList[docIndex].isChecked;
        setVerifiedProducts([...verifiedProducts]);
    };
    const removeProduct = (index: number, productIndex: number, inputRef: React.MutableRefObject<HTMLInputElement | null>) => {
        verifiedProducts[index].documentList?.splice(productIndex, 1);
        verifiedProducts[index].documentList?.length === 0 && verifiedProducts.splice(index, 1);
        setVerifiedProducts([...verifiedProducts]);
        if (verifiedProducts.length === 0) {
            inputRef.current?.focus();
        }
    };

    const addressItems = verifiedProducts.map((product, index) => (
        <li>
            <label>
                {`${
                    propertyType === PropertyType.community
                        ? communityNameLabel
                        : propertyType === PropertyType.quadrant
                        ? quadrantLabel
                        : propertyType === PropertyType.ats
                        ? atsLabel
                        : blockProfileLabel
                }: ` + product.searchKey}
            </label>
            <ul>
                {product.documentList.map((doc, docIndex) => (
                    <li>
                        <label>
                            <input type='checkbox' defaultChecked={doc.isChecked} onChange={() => onProductClickChange(index, docIndex)} />
                            {`${doc.documentName}`}
                            <button className='removeItem' onClick={() => removeProduct(index, docIndex, inputRef)}>
                                {removeLabel}
                            </button>
                        </label>
                    </li>
                ))}
            </ul>
        </li>
    ));

    const renderDocList = () => {
        if (docsList.length >= 1) {
            return (
                <div>
                    <div className='result-wrap'>
                        <h3>{restProps.resources.modalDocumentList}</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>{restProps.resources.selectTextLabel}</th>
                                    <th>{restProps.resources.tableDocumentName}</th>
                                    <th>{restProps.resources.priceLabel}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {docsList.map(doc => {
                                    return (
                                        <tr>
                                            <td>
                                                <input
                                                    type='checkbox'
                                                    name=''
                                                    id={doc.documentId}
                                                    checked={doc.isChecked}
                                                    onChange={() => handleDocumentCheck(doc)}
                                                />
                                            </td>
                                            <td>{doc.documentName}</td>
                                            <td>{restProps.context.cultureFormatter.formatCurrency(product?.Price!)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className='btn-box'>
                        <button className='cui utility-btn-solid' type='button' disabled={disableAddNewItemBtn()} onClick={onAddNewItem}>
                            {restProps.resources.addNewItemLabel}
                        </button>
                    </div>
                </div>
            );
        }
        if (documentListErrorMessage) {
            return (
                <p aria-live='assertive' aria-label={documentListErrorMessage} role='alert' className='cui alert-small error'>
                    {documentListErrorMessage}
                </p>
            );
        }
        return;
    };
    const disableAddNewItemBtn = () => {
        return !docsList.some(doc => doc.isChecked === true);
    };

    return (
        <Modal
            role='document'
            aria-labelledBy='productSearchTitle'
            className='cui modal-lg'
            isOpen={isDCModalOpen}
            toggle={closeDCModal}
            keyboard={true}
        >
            <div className='modal-header'>
                <h2 id='productSearchTitle' tabIndex={0} className='modal-title'>
                    {defaultFormHeader}
                </h2>
                <button className='btn-modal-close cui btn-md primary-text' onClick={closeDCModal}>
                    {closeModalLabel}
                </button>
            </div>
            <ModalBody>
                <div className='property-search-wrap'>
                    <h3>{product?.Name}</h3>
                    <form onSubmit={e => e.preventDefault()}>
                        <div className='form-group'>
                            {propertyType === PropertyType.quadrant ? (
                                <div>
                                    <label htmlFor='quadrant'>{restProps.resources.quadrantLabel}</label>
                                    <select
                                        className='form-field'
                                        name='quadrant'
                                        id='quadrant'
                                        required
                                        value={dcFields.value}
                                        onChange={handleQuadrantChange}
                                    >
                                        <option value=''>{selectQuadrantsLabel}</option>
                                        <option value={`${selectQuadrantsValNE}`}>{selectQuadrantsValNE}</option>
                                        <option value={`${selectQuadrantsValNW}`}>{selectQuadrantsValNW}</option>
                                        <option value={`${selectQuadrantsValSW}`}>{selectQuadrantsValSW}</option>
                                        <option value={`${selectQuadrantsValSE}`}>{selectQuadrantsValSE}</option>
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <TypeAheadInput
                                        label={
                                            <label htmlFor='communityName'>
                                                {propertyType === PropertyType.community
                                                    ? communityNameLabel
                                                    : propertyType === PropertyType.ats
                                                    ? atsLabel
                                                    : blockProfileLabel}
                                                <span className='starick'>{restProps.resources.requiredText}</span>
                                            </label>
                                        }
                                        minimumInput={2}
                                        name='communityName'
                                        errorID='searchDescription'
                                        getOptions={getOptionGetter()}
                                        onOptionSelection={handleCommunityNameSelection}
                                        query={dcFields.value}
                                        onQueryChange={handleCommunityChange}
                                        errorMessage={dcFields.errorMessage}
                                        preventTab={!(dcFields.isOptionSelected || verifiedProducts.length)}
                                        inputRef={inputRef}
                                    />
                                </div>
                            )}
                            {searchInstructions && (
                                <div
                                    aria-live='assertive'
                                    aria-label={searchInstructions}
                                    role='alert'
                                    id='searchDescription'
                                    className='cui alert-banner global information search-instructions'
                                >
                                    <RichTextComponent text={searchInstructions} />
                                </div>
                            )}
                            <button
                                type='button'
                                className='cui utility-btn-solid'
                                onClick={onSearchClick}
                                disabled={!dcFields.isOptionSelected}
                                ref={staticSearchBtnRef}
                            >
                                {restProps.resources.searchBtnText}
                            </button>
                            {renderDocList()}
                            {errorMessages.map(msg => (
                                <p aria-live='assertive' aria-label={msg} role='alert' className='cui alert-small error'>
                                    {msg}
                                </p>
                            ))}
                            <div className='result-wrap'>
                                <strong>
                                    {restProps.resources.selectedItemsLabel}
                                    {addressItems && addressItems.length ? <span>{` (${addressItems.length}):`}</span> : ':'}
                                </strong>
                                {addressItems && addressItems.length ? null : <p>{noItemsSelectedLabel}</p>}
                                <ul>{addressItems}</ul>
                            </div>
                            {/* Render Community Names options */}
                        </div>
                    </form>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className='btn-box'>
                    <button
                        type='button'
                        disabled={!verifiedProducts.some(prod => prod.documentList.some(doc => doc.isChecked === true))}
                        className='cui btn-md primary'
                        onClick={handleDocumentsCategroyFormSubmission}
                    >
                        <span className='btn-wrapper'>
                            <span className='btn-text'>{addToCartButtonLabel}</span>
                            <span className='cicon-angle-right right'></span>
                        </span>
                    </button>
                    {showLoader && <Spinner text={loadingLabel} />}
                </div>
            </ModalFooter>
        </Modal>
    );
};
export default GenericForm;
